// Experience card component with expandable AI context

import { useState } from 'react';

interface ExperienceCardProps {
  companyName: string;
  title: string;
  titleProgression?: string;
  startDate?: string;
  endDate?: string;
  isCurrent: boolean;
  bulletPoints: string[];
  // AI context (optional - shown when expanded)
  aiContext?: {
    situation?: string;
    approach?: string;
    technicalWork?: string;
    lessonsLearned?: string;
  };
}

export function ExperienceCard({
  companyName,
  title,
  titleProgression,
  startDate,
  endDate,
  isCurrent,
  bulletPoints,
  aiContext,
}: ExperienceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (date?: string) => {
    if (!date) return 'Present';
    const [year, month] = date.split('-').map(Number);
    return new Date(year, month - 1).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  };

  const dateRange = `${formatDate(startDate)} - ${isCurrent ? 'Present' : formatDate(endDate)}`;

  return (
    <div className="card-hover mb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
        <div>
          <h3 className="text-xl font-serif font-semibold text-accent">
            {companyName}
          </h3>
          <p className="text-text-primary font-medium">{title}</p>
          {titleProgression && (
            <p className="text-sm text-text-secondary">{titleProgression}</p>
          )}
        </div>
        <span className="text-sm text-text-muted whitespace-nowrap">
          {dateRange}
        </span>
      </div>

      {/* Bullet points */}
      <ul className="space-y-2 mb-4">
        {bulletPoints.map((point, index) => (
          <li key={index} className="flex gap-2 text-text-secondary">
            <span className="text-accent mt-1.5">→</span>
            <span>{point}</span>
          </li>
        ))}
      </ul>

      {/* AI Context toggle (only show if we have context) */}
      {aiContext && Object.values(aiContext).some(Boolean) && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm text-text-muted hover:text-accent transition-colors"
          >
            <span className="text-lg">✨</span>
            <span>{isExpanded ? 'Hide AI Context' : 'Show AI Context'}</span>
            <svg
              className={`w-4 h-4 transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Expanded AI context */}
          {isExpanded && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-border/30 space-y-4 animate-fade-in">
              {aiContext.situation && (
                <div>
                  <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wide mb-1">
                    Situation
                  </h4>
                  <p className="text-sm text-text-secondary">
                    {aiContext.situation}
                  </p>
                </div>
              )}
              {aiContext.approach && (
                <div>
                  <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wide mb-1">
                    Approach
                  </h4>
                  <p className="text-sm text-text-secondary">
                    {aiContext.approach}
                  </p>
                </div>
              )}
              {aiContext.technicalWork && (
                <div>
                  <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wide mb-1">
                    Technical Work
                  </h4>
                  <p className="text-sm text-text-secondary">
                    {aiContext.technicalWork}
                  </p>
                </div>
              )}
              {aiContext.lessonsLearned && (
                <div>
                  <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wide mb-1">
                    Lessons Learned
                  </h4>
                  <p className="text-sm text-text-secondary italic">
                    {aiContext.lessonsLearned}
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
